/**
 * /checkin/:gym_id/:token — Arena check-in landing page (the gym's static QR).
 * Confirms with user, calls backend, shows the wow overlay.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, MapPin } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { ArenaShield } from "../../components/arena/ArenaAtoms";
import { toast } from "sonner";

export default function ArenaCheckin() {
  const { gym_id, token } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [gym, setGym] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [overlayData, setOverlayData] = useState(null);

  useEffect(() => {
    if (!user) {
      sessionStorage.setItem("post_login_redirect", `/checkin/${gym_id}/${token}`);
      navigate("/login");
      return;
    }
    apiClient.get(`/arena/gyms/${gym_id}`)
      .then((r) => setGym(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Gym not found"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gym_id, token]);

  const doCheckin = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/arena/checkin", { gym_id, token });
      setOverlayData(data);
      // Auto-dismiss after 3 seconds → home (which will be in Arena Mode)
      setTimeout(() => navigate("/home", { replace: true }), 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check in");
    } finally { setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen grid place-items-center px-6" style={{ background: "var(--bg-base, #ECEAE6)" }}>
      <div style={{ textAlign: "center" }}>
        <h2 style={{ fontFamily: "Barlow Condensed", fontSize: 24, fontWeight: 800 }}>Check-in unavailable</h2>
        <p style={{ fontSize: 13, color: "var(--text-tertiary, #8A8884)", marginTop: 6 }}>{error}</p>
        <button onClick={() => navigate("/home")} style={{ marginTop: 14, padding: "10px 18px", background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontWeight: 700 }}>
          Back to Home
        </button>
      </div>
    </div>
  );

  if (!gym) return <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg-base, #ECEAE6)" }}>Loading…</div>;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-base, #ECEAE6)" }} data-testid="arena-checkin-page">
      <header style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--bg-elevated, #F0EEEA)", borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))" }}>
        <div className="max-w-md mx-auto flex items-center px-4 py-3">
          <button onClick={() => navigate("/home")} aria-label="Back" style={{ background: "transparent", border: "none" }}>
            <ChevronLeft size={22} />
          </button>
          <ArenaShield />
        </div>
      </header>
      <main className="max-w-md mx-auto px-5 pt-6 pb-32">
        <h1 style={{ fontFamily: "Barlow Condensed", fontSize: 28, fontWeight: 900 }}>Check in to {gym.gym_name}?</h1>
        <p style={{ fontSize: 13, color: "var(--text-tertiary, #8A8884)", marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4 }}>
          <MapPin size={12} /> {gym.city}
        </p>
        {gym.photos?.[0] && (
          <div style={{ marginTop: 14, aspectRatio: "16/9", borderRadius: 16, overflow: "hidden" }}>
            <img src={gym.photos[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        )}
        <button onClick={doCheckin} disabled={busy} data-testid="checkin-confirm-btn"
          style={{ width: "100%", height: 52, marginTop: 18, background: "#F5C518", color: "#0C0C0B", border: "none", borderRadius: 100, fontFamily: "Barlow", fontSize: 14, fontWeight: 800 }}>
          {busy ? "Checking in…" : "Check In"}
        </button>
        <p style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", textAlign: "center", marginTop: 8 }}>+10 FitPoints · Auto-checkout in 4 hours</p>
      </main>

      <AnimatePresence>
        {overlayData && <CheckinOverlay data={overlayData} />}
      </AnimatePresence>
    </div>
  );
}

export function CheckinOverlay({ data }) {
  const { gym, trainer_note, fitpoints_awarded, credits_remaining } = data || {};
  const dt = new Date().toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120]"
      style={{
        background: "linear-gradient(160deg, #2A2825 0%, #1A1A18 60%, #0C0C0B 100%)",
        display: "grid", placeItems: "center", padding: 24,
      }}
      data-testid="checkin-overlay"
    >
      <div style={{ position: "absolute", inset: 0, opacity: 0.04, backgroundImage: "radial-gradient(circle at 30% 20%, rgba(245,197,24,0.6), transparent 60%)" }} aria-hidden />
      <div style={{ position: "relative", color: "white", maxWidth: 380, width: "100%", textAlign: "center" }}>
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
          style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}
        >
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", inset: -16, background: "rgba(245,197,24,0.30)", borderRadius: "50%", filter: "blur(24px)" }} />
            <img src="/assets/arena/fitfrik-shield.jpg" alt="" width={48} height={48} style={{ position: "relative", borderRadius: "50%", objectFit: "cover" }} />
          </div>
        </motion.div>
        <div style={{ fontFamily: "Barlow", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.2em", color: "rgba(255,255,255,0.50)" }}>
          Arena Mode
        </div>
        <motion.div
          initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.45, delay: 0.15 }}
          style={{ fontFamily: "Barlow Condensed", fontSize: 36, fontWeight: 900, lineHeight: 1.05, color: "white", marginTop: 6 }}
        >
          {gym?.gym_name}
        </motion.div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.50)", marginTop: 6 }}>{dt}</div>
        {trainer_note && (
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.10)" }}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
              {trainer_note.trainer_avatar ? (
                <img src={trainer_note.trainer_avatar} alt="" width={28} height={28} style={{ borderRadius: "50%", objectFit: "cover" }} />
              ) : (
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(245,197,24,0.18)", color: "#F5C518", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 800 }}>
                  {(trainer_note.trainer_name || "T").slice(0, 1)}
                </div>
              )}
              <span style={{ fontSize: 12, fontWeight: 600, color: "white" }}>{trainer_note.trainer_name || "Your trainer"}</span>
            </div>
            <p style={{ fontSize: 14, fontWeight: 500, color: "#F5C518", marginTop: 8 }}>"{trainer_note.note_text}"</p>
          </div>
        )}
        <div style={{ marginTop: 24, display: "flex", justifyContent: "center", gap: 14, fontSize: 12, color: "#F5C518", fontWeight: 700 }}>
          <span>+{fitpoints_awarded || 10} FitPoints</span>
          {credits_remaining != null && <span>· Arena Pass: {credits_remaining} left</span>}
        </div>
      </div>
    </motion.div>
  );
}
